import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowRight, Cloud, Server, Zap, Wrench, Layers, Palette, Check, Sparkles, ChevronRight } from 'lucide-react';
import ColorAmbient from '../components/ColorAmbient';
import PrinterStrip from '../components/PrinterStrip';
import RegistrationMark from '../components/RegistrationMark';

const ACCENT = '#00B7C7';

const modules = [
  { name: 'Editor', desc: 'Prepare files, impose and preflight in one clean interface.', Icon: Palette },
  { name: 'Server', desc: 'On-prem color server that automates job routing and profiling.', Icon: Server },
  { name: 'Cloud', desc: 'Multi-site cloud color management with real-time synchronization.', Icon: Cloud },
  { name: 'Deploy', desc: 'Push calibrations and profiles to every device in your fleet.', Icon: Zap },
  { name: 'Fire (RIP)', desc: 'High-performance RIP engine for wide-format inkjet devices.', Icon: Wrench },
  { name: 'Verify', desc: 'Automated color verification with ISO 12647 tolerance reports.', Icon: Check },
];

const features = [
  'Automatic press calibration in minutes',
  'Multi-device output with single color library',
  'Real-time color server synchronization',
  'Compatible with 400+ printers and cutters',
  'ISO 12647 & G7 workflow certification',
  'Cloud-based subscription with local deployment',
];

const PrintFactoryPage = () => {
  return (
    <main className="pt-28 pb-24 bg-white min-h-screen relative overflow-hidden">
      <ColorAmbient variant="cool" intensity="medium" />

      {/* Registration marks */}
      <RegistrationMark className="absolute top-24 left-6 opacity-40" size={24} />
      <RegistrationMark className="absolute top-24 right-6 opacity-40" size={24} />

      {/* Hero */}
      <section className="container-pn relative">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/70 backdrop-blur border border-[#0A2A63]/10 text-[#0A2A63] mb-6 shadow-sm">
              <Sparkles className="h-3.5 w-3.5" style={{ color: ACCENT }} />
              <span className="text-xs font-semibold tracking-widest uppercase">Star Product</span>
            </div>
            <img
              src="/logos/printfactory.png"
              alt="PrintFactory"
              className="h-20 mb-6 object-contain object-left"
            />
            <h1 className="text-5xl md:text-7xl font-black text-[#050F2E] tracking-tight leading-[0.95]">
              Software written by{' '}
              <span className="pn-gradient-text">
                printers
              </span>, for printers
            </h1>
            <p className="mt-6 text-lg text-slate-600 max-w-xl leading-relaxed">
              A complete color management workflow that runs on 150,000+ presses worldwide.
              Calibrate, profile, and automate output across your entire fleet.
            </p>

            <div className="mt-8 max-w-md">
              <PrinterStrip className="opacity-90" />
            </div>

            <div className="mt-9 flex flex-wrap items-center gap-4">
              <Link to="/contact">
                <Button className="bg-[#0A2A63] hover:bg-[#061a3f] text-white rounded-full h-13 px-7 font-bold btn-transition">
                  Request a demo <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </Link>
              <Link to="/shop">
                <Button variant="outline" className="rounded-full h-13 px-7 font-bold border-2 border-[#0A2A63] text-[#0A2A63] hover:bg-[#0A2A63] hover:text-white btn-transition bg-white/70 backdrop-blur">
                  See compatible hardware
                </Button>
              </Link>
            </div>

            {/* Stats */}
            <div className="mt-14 grid grid-cols-3 max-w-lg gap-4">
              <div>
                <p className="text-3xl md:text-4xl font-black text-[#0A2A63]">150k+</p>
                <p className="text-xs uppercase tracking-widest text-slate-500 mt-1">Presses</p>
              </div>
              <div className="border-l border-slate-200 pl-4">
                <p className="text-3xl md:text-4xl font-black text-[#0A2A63]">400+</p>
                <p className="text-xs uppercase tracking-widest text-slate-500 mt-1">Devices</p>
              </div>
              <div className="border-l border-slate-200 pl-4">
                <p className="text-3xl md:text-4xl font-black text-[#0A2A63]">75+</p>
                <p className="text-xs uppercase tracking-widest text-slate-500 mt-1">Countries</p>
              </div>
            </div>
          </div>

          {/* Right visual */}
          <div className="lg:col-span-5 relative h-[520px]">
            <div className="absolute inset-0 rounded-[28px] overflow-hidden shadow-2xl bg-gradient-to-br from-[#0A2A63] to-[#061a3f]">
              <img
                src="https://images.unsplash.com/photo-1693031630369-bd429a57f115?w=1200&q=85"
                alt="PrintFactory workflow"
                className="absolute inset-0 w-full h-full object-cover opacity-70"
              />
              <div className="absolute inset-0 grain-overlay opacity-40" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#050F2E] via-transparent to-transparent" />

              <div className="absolute top-5 right-5 bg-white/95 backdrop-blur-sm rounded-2xl p-4 shadow-xl">
                <p className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Setup time</p>
                <p className="text-2xl font-black text-[#0A2A63]">-60<span className="text-[#FF4E50]">%</span></p>
              </div>
              <div className="absolute bottom-6 left-6 right-6">
                <PrinterStrip />
              </div>
            </div>
            <div className="absolute -bottom-4 -right-4 h-24 w-24 rounded-full border-2 border-dashed border-[#FF4E50] animate-spin-slow opacity-70" />
          </div>
        </div>
      </section>

      {/* Modules grid */}
      <section className="container-pn mt-28 relative">
        <div className="max-w-3xl mb-12">
          <p className="text-xs uppercase tracking-[0.3em] text-[#FF4E50] font-bold mb-3">Modules</p>
          <h2 className="text-4xl md:text-5xl font-black text-[#050F2E] tracking-tight">Six modules. One color truth.</h2>
          <p className="mt-4 text-slate-600">Choose the modules that fit your operation. Every module speaks the same language and shares the same color engine.</p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {modules.map(({ name, desc, Icon }, idx) => (
            <div key={name} className="group bg-white rounded-2xl p-7 border border-slate-100 hover:border-[#0A2A63] hover:shadow-2xl btn-transition">
              <div className="flex items-center justify-between mb-4">
                <span className="h-11 w-11 rounded-xl flex items-center justify-center" style={{ backgroundColor: 'rgba(0,183,199,0.14)' }}>
                  <Icon className="h-5 w-5" style={{ color: ACCENT }} />
                </span>
                <span className="text-xs font-mono text-slate-300">0{idx + 1}</span>
              </div>
              <h3 className="text-xl font-bold text-[#050F2E]">{name}</h3>
              <p className="text-sm text-slate-600 mt-2 leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Features list */}
      <section className="container-pn mt-24 relative">
        <div className="grid lg:grid-cols-12 gap-10 items-center bg-[#0A2A63] text-white rounded-3xl p-10 md:p-14 shadow-2xl overflow-hidden relative">
          <div className="absolute -top-24 -right-24 h-80 w-80 rounded-full bg-[#00B7C7]/25 blur-3xl" />
          <div className="absolute -bottom-24 -left-24 h-72 w-72 rounded-full bg-[#FF4E50]/15 blur-3xl" />

          <div className="lg:col-span-6 relative">
            <p className="text-xs uppercase tracking-[0.3em] text-[#FF4E50] font-bold mb-3">Why it wins</p>
            <h2 className="text-3xl md:text-4xl font-black tracking-tight leading-tight">A workflow that pays for itself in the first quarter.</h2>
            <p className="mt-4 text-slate-300">Real ROI, real numbers. Below the highlights our customers see within the first 90 days.</p>
            <div className="mt-8 grid grid-cols-2 gap-4">
              <div className="bg-white/5 rounded-2xl p-5 border border-white/10">
                <p className="text-3xl font-black">-60%</p>
                <p className="text-xs text-slate-400 mt-1">Setup time</p>
              </div>
              <div className="bg-[#FF4E50] text-[#050F2E] rounded-2xl p-5">
                <p className="text-3xl font-black">ΔE &lt; 1</p>
                <p className="text-xs font-semibold mt-1">Print consistency</p>
              </div>
              <div className="bg-white/5 rounded-2xl p-5 border border-white/10">
                <p className="text-3xl font-black">2.5x</p>
                <p className="text-xs text-slate-400 mt-1">Faster job change</p>
              </div>
              <div className="bg-white text-[#050F2E] rounded-2xl p-5">
                <p className="text-3xl font-black">400+</p>
                <p className="text-xs font-semibold mt-1">Supported devices</p>
              </div>
            </div>
          </div>

          <div className="lg:col-span-6 relative">
            <ul className="space-y-3">
              {features.map((f) => (
                <li key={f} className="flex items-start gap-3 bg-white/5 hover:bg-white/10 transition-colors rounded-xl p-4 border border-white/10">
                  <span className="h-9 w-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'rgba(0,183,199,0.35)' }}>
                    <Check className="h-4 w-4" style={{ color: '#a7f3f8' }} />
                  </span>
                  <span className="font-semibold text-white pt-1.5">{f}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="container-pn mt-20 relative">
        <div className="rounded-3xl bg-white border border-slate-100 p-10 md:p-14 shadow-xl overflow-hidden relative">
          <div className="absolute -top-20 -right-20 h-72 w-72 rounded-full bg-[#00B7C7]/20 blur-3xl" />
          <div className="relative grid md:grid-cols-2 gap-8 items-center">
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-[#FF4E50] font-bold mb-3">Ready to transform your workflow?</p>
              <h2 className="text-3xl md:text-4xl font-black tracking-tight text-[#050F2E]">Talk to a PrintFactory specialist.</h2>
              <p className="mt-3 text-slate-600">We&apos;ll audit your current setup and recommend the modules that fit your operation.</p>
            </div>
            <div className="flex md:justify-end flex-wrap gap-3">
              <Link to="/contact">
                <Button className="bg-[#0A2A63] hover:bg-[#061a3f] text-white rounded-full h-12 px-7 font-bold btn-transition">
                  Get in touch <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </Link>
              <Link to="/impoproof">
                <Button variant="outline" className="rounded-full h-12 px-7 font-bold border-2 border-[#0A2A63] text-[#0A2A63] hover:bg-[#0A2A63] hover:text-white btn-transition">
                  See ImpoProof
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default PrintFactoryPage;
